import { computed, effect, inject, Injectable, linkedSignal, resource, signal } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { NGXLogger } from 'ngx-logger';
import { filter } from 'rxjs';

import { EnvironmentService } from '../../environments/environment.service';
import { AuthInfo } from '../awp/auth-info';
import { AuthHeadersService } from './auth-headers.service';
import { WenClient } from './wen.client';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly location = inject(Location);
  private readonly history = inject(History);
  private readonly oAuthService = inject(OAuthService);
  private readonly wenClient = inject(WenClient);
  private readonly logger = inject(NGXLogger);
  private readonly environmentService = inject(EnvironmentService);
  private readonly authHeadersService = inject(AuthHeadersService);

  // Signal for the authinfo object in the AWP integration. Gets updated in case the access token changes.
  // Stays undefined and never gets updated in the s-gate integration scenario
  readonly awpAuthInfoInput = signal<AuthInfo | undefined>(undefined);

  // Signal for the selected business partner in the AWP integration. Gets updated when the outlet changes.
  // Stays undefined and never gets updated if not in AWP.
  readonly awpBusinessPartnerIdInput = signal<string | undefined>(undefined);

  readonly adminDistributionPartnerNumber = signal<string | undefined>(undefined);

  /**
   * The path to use for the OAuth redirect URI, relative to the origin.
   *
   * This path must be registered in WEN for the client ID used.
   */
  readonly oauthRedirectPath = signal<string | undefined>(undefined);

  /** Initial login to WEN, only performed if no AWP authinfo is provided */
  private readonly wenInitialLogin = resource({
    // only perform the request if the authinfo object is not set
    params: () => (this.awpAuthInfoInput() ? undefined : true),
    loader: async (): Promise<true> => {
      try {
        const jwks = await this.wenClient.getJwks();
        this.oAuthService.configure(this.authConfigByOrigin(jwks));
        await this.oAuthService.tryLogin();
        this.removeOidcRedirectParametersFromUrl();
        return true;
      } catch (error) {
        this.logger.error('#CDFTF0221 Error during WEN initial login:', error);
        throw error;
      }
    },
  });

  /**
   * WEN access token
   *
   * This is only requested if no AWP authinfo is provided, and once the parameters have been extracted from the URL.
   *
   * It can be manually refreshed when a token refresh occurs due to the previous token expiring.
   *
   * When the token is reloading, this is temporarily undefined.
   * Use `stableWenToken` to keep the previous value until the new token is available,
   * so that downstream consumers don't briefly see `undefined` and flash a loading indicator.
   */
  private readonly wenToken = resource({
    // only perform the request if the initial login has completed
    params: () => (this.wenInitialLogin.hasValue() || this.wenInitialLogin.error() ? true : undefined),
    loader: async () => {
      const error = this.wenInitialLogin.error();
      if (error) {
        throw error;
      }
      if (this.oAuthService.hasValidAccessToken()) {
        return this.oAuthService.getAccessToken();
      } else {
        this.oAuthService.initLoginFlow();
        return new Promise<never>(() => {}); // wait indefinitely - initLoginFlow will redirect
      }
    },
  });

  /**
   * Persists the last valid WEN token value across resource reloads (e.g. during token refresh),
   * so that downstream consumers don't briefly see `undefined` and flash a loading indicator.
   * Clears on error so that error states propagate correctly.
   */
  private readonly stableWenToken = linkedSignal<{ value: string | undefined; error: unknown }, string | undefined>({
    source: () => ({ value: this.wenToken.value(), error: this.wenToken.error() }),
    computation: (source, previous) => {
      if (source.error) {
        return undefined;
      }
      return source.value ?? previous?.value;
    },
  });

  public readonly authHeadersWithoutAdminDpNr = computed(() => {
    const authInfo = this.awpAuthInfoInput();
    const wenToken = this.stableWenToken();

    const bearerToken = authInfo ? authInfo.wenOidcAccessToken : wenToken;

    if (!bearerToken) {
      return undefined;
    }

    const businessPartnerId = this.awpBusinessPartnerIdInput();
    const businessPartnerHeader = businessPartnerId ? [['Auth-Business-Partner-Id', businessPartnerId] as const] : [];

    return new Map([...businessPartnerHeader, ['Authorization', `Bearer ${bearerToken}`]]);
  });

  public readonly authHeaders = computed(() => {
    const originalHeaders = this.authHeadersWithoutAdminDpNr();
    if (!originalHeaders) {
      return undefined;
    }

    const adminDpNr = this.adminDistributionPartnerNumber();
    const adminDpNrHeader = adminDpNr ? [['Auth-Admin-Distribution-Partner', adminDpNr] as const] : [];
    return new Map([...originalHeaders, ...adminDpNrHeader]);
  });

  public readonly error = computed(() => this.wenToken.error());

  protected readonly logWenTokenError = effect(() => {
    const error = this.error();
    if (error) {
      this.logger.error('#CDFFF0163 Error retrieving access token: ', error);
    }
  });

  protected readonly updateAuthHeaders = effect(() => {
    const authHeaders = this.authHeaders();
    this.authHeadersService.authHeaders.set(authHeaders);
  });

  constructor() {
    this.oAuthService.tokenValidationHandler = new JwksValidationHandler();
    this.oAuthService.setupAutomaticSilentRefresh();

    this.oAuthService.events.pipe(filter(({ type }) => type === 'token_received')).subscribe(() => {
      // If this event was received through a token refresh rather than by explicitly reloading
      if (!this.wenToken.isLoading()) {
        // Refresh the wenToken resource to get the new access token
        this.wenToken.reload();
      }
    });
  }

  logOut() {
    this.oAuthService.logOut();
  }

  private removeOidcRedirectParametersFromUrl() {
    const currentUrl = new URL(this.location.href);
    const searchParamKeys = [...currentUrl.searchParams.keys()];
    for (const key of searchParamKeys) {
      currentUrl.searchParams.delete(key);
    }
    const sanitizedUrl = currentUrl.href;
    this.history.replaceState(this.history.state, '', sanitizedUrl);
  }

  private authConfigByOrigin(jwks: object): AuthConfig {
    const origin = this.location.origin;
    const redirectPath = this.oauthRedirectPath() || '/';
    const redirectUri = new URL(redirectPath, origin).href;
    const domain = this.environmentService.env.domains[origin];
    if (!domain) {
      throw new Error('OIDC Strategy used at invalid domain. This is likely an implementation error.');
    }

    return {
      issuer: domain.issuer,
      loginUrl: `${domain.issuer}/authorize`,
      redirectUri,
      logoutUrl: `${domain.issuer}/connect/endSession`,
      postLogoutRedirectUri: `${origin}/`,
      tokenEndpoint: `${origin}/api/customer-data-frontend/wen/access_token`,
      clientId: domain.clientId,
      jwks,
      scope: 'openid profile organization email b2xroles b2d address',
      responseType: 'code',
      oidc: true,
      customQueryParams: {
        // See https://atc.bmwgroup.net/confluence/display/WEBEAM/WEN+OpenID+Connect+%28OIDC%29+-+Integration+Guide#WENOpenIDConnect(OIDC)IntegrationGuide-AuthenticationServices
        acr_values: 'strongAuth4000Service',
      },
    };
  }
}
